/**
 * LoginSuccessPage — React version of the OAuth callback landing page (D-17).
 *
 * Phase 3 `/auth/callback` redirects to `/login-success#token=<jwt>` (token in
 * the URL FRAGMENT, not query — fragments never hit the server). On mount we:
 *   1. Parse `#token=...` from window.location.hash.
 *   2. Call `setAuth(token)` to decode + persist to localStorage.
 *   3. Strip the hash from the URL via history.replaceState.
 *   4. Navigate to `/` (replace, so Back doesn't return to /login-success).
 *
 * If the hash is missing, invalid, or decode fails, we surface an error with a
 * "Try again" link back to /salescoach/login. This lets the user restart the
 * handshake rather than sit on a broken page.
 */

import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '@/context/AuthContext';

export function LoginSuccessPage() {
  const { setAuth, isAuthenticated } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const hash = location.hash || window.location.hash;
    if (!hash || hash.indexOf('#token=') !== 0) {
      setError('Missing token in URL fragment');
      return;
    }
    const rawToken = hash.substring('#token='.length);
    const token = decodeURIComponent(rawToken);
    if (!token) {
      setError('Empty token');
      return;
    }
    setAuth(token);
    // Strip hash so the JWT isn't visible in the address bar after redirect.
    try {
      window.history.replaceState(null, '', window.location.pathname);
    } catch {
      // noop — non-browser environments
    }
  }, [location.hash, setAuth]);

  // After setAuth succeeds the provider flips isAuthenticated → true. This
  // effect listens for that transition and performs the redirect. Keeping
  // navigation out of the decode-effect prevents navigating before React
  // commits the new auth state, which would race with <RequireAuth>.
  useEffect(() => {
    if (isAuthenticated && !error) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, error, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="bg-card border rounded-lg shadow-sm p-8 max-w-md w-full text-center">
        {error ? (
          <>
            <h1 className="text-xl font-semibold mb-2">Sign-in failed</h1>
            <p
              className="text-sm text-destructive mb-4"
              data-testid="login-success-error"
            >
              {error}
            </p>
            <a
              href="/salescoach/login"
              className="text-sm text-primary underline"
            >
              Try again
            </a>
          </>
        ) : (
          <p
            className="text-sm text-muted-foreground"
            data-testid="login-success-loading"
          >
            Signing you in&hellip;
          </p>
        )}
      </div>
    </div>
  );
}
